import React, { useMemo } from "react";
import { Button, DeleteIcon, PlusIcon } from "@pd/pd-ui-kit";
import { Divider, Flex } from "antd";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  COMBINATION_RULE_CONDITION_TYPES,
  CombinationRuleConditionType,
} from "~/entity/CombinationRules";
import { Field } from "~/shared/ui/Field";

import {
  emptyCondition,
  RuleConditionFormValue,
  RuleFormSelectValue,
  RuleGroupFormValue,
} from "./options";

type Props = {
  control: Control<any>;
  groupIndex: number;
  errors: any;
  postTypeOptions: RuleFormSelectValue[];
  categoryOptions: RuleFormSelectValue[];
  operatorOptions: RuleFormSelectValue[];
  isLoadingPostTypes: boolean;
  isView: boolean;
};

const GroupConditions = ({
  control,
  groupIndex,
  errors,
  postTypeOptions,
  categoryOptions,
  operatorOptions,
  isLoadingPostTypes,
  isView,
}: Props) => {
  const { t } = useTranslation();
  const name = `groups[${groupIndex}].conditions` as const;

  const { fields, append, remove } = useFieldArray({ control, name });

  const groupsValue =
    (useWatch({
      control,
      name: "groups",
      defaultValue: control.getValues("groups") ?? [],
    }) as RuleGroupFormValue[]) ?? [];
  const conditionsValue = groupsValue[groupIndex]?.conditions ?? [];

  const usedTypes = conditionsValue
    .map((condition) => condition?.conditionType?.value)
    .filter(Boolean) as CombinationRuleConditionType[];

  const conditionTypeOptionsAll = useMemo(
    () =>
      COMBINATION_RULE_CONDITION_TYPES.map((value) => ({
        value,
        label: t(`combinationRules.conditionTypes.${value}`),
      })),
    [t],
  );

  const optionsForRow = (rowIndex: number): RuleFormSelectValue[] => {
    const currentValue = conditionsValue[rowIndex]?.conditionType?.value;
    return conditionTypeOptionsAll.filter(
      ({ value }) =>
        value === currentValue ||
        !usedTypes.includes(value as CombinationRuleConditionType),
    );
  };

  const conditionErrors = (errors?.groups as any)?.[groupIndex]?.conditions;

  return (
    <Flex vertical gap={10}>
      {fields.map((field, conditionIndex) => {
        const condition = conditionsValue[conditionIndex] ?? {};
        const conditionType = condition.conditionType?.value as
          | CombinationRuleConditionType
          | undefined;
        const rowErrors = conditionErrors?.[conditionIndex];

        return (
          <React.Fragment key={field.id}>
            {conditionIndex > 0 && <Divider style={{ margin: 0 }} />}
            <Flex vertical gap={10}>
              <Flex gap={10} align="flex-start">
                <div style={{ flex: 1 }}>
                  <Field
                    type="select"
                    name={`${name}[${conditionIndex}].conditionType`}
                    control={control}
                    defaultValue={condition.conditionType ?? null}
                    label={`${t("combinationRules.conditionType")} *`}
                    options={optionsForRow(conditionIndex)}
                    labelInValue
                    disabled={isView}
                    error={
                      rowErrors?.conditionType?.value?.message ??
                      rowErrors?.conditionType?.message
                    }
                  />
                </div>
                {!isView && (
                  <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    htmlType="button"
                    icon={<DeleteIcon size={16} />}
                    aria-label={t("ui.delete")}
                    title={t("ui.delete")}
                    style={{ marginTop: 22 }}
                    onClick={() => remove(conditionIndex)}
                  />
                )}
              </Flex>

              {conditionType === "posts" && (
                <Field
                  type="select"
                  name={`${name}[${conditionIndex}].postIds`}
                  control={control}
                  defaultValue={condition.postIds ?? []}
                  label={`${t("combinationRules.posts")} *`}
                  options={postTypeOptions}
                  mode="multiple"
                  labelRender={(selected) => selected.label}
                  labelInValue
                  loading={isLoadingPostTypes}
                  disabled={isView}
                  error={rowErrors?.postIds?.message}
                />
              )}

              {conditionType === "category" && (
                <Field
                  type="select"
                  name={`${name}[${conditionIndex}].category`}
                  control={control}
                  defaultValue={condition.category ?? []}
                  label={`${t("combinationRules.category")} *`}
                  options={categoryOptions}
                  mode="multiple"
                  labelRender={(selected) => selected.label}
                  labelInValue
                  disabled={isView}
                  error={rowErrors?.category?.message}
                />
              )}

              {conditionType && conditionType !== "category" && (
                <Flex gap={10}>
                  <div style={{ flex: 1 }}>
                    <Field
                      type="select"
                      name={`${name}[${conditionIndex}].operator`}
                      control={control}
                      defaultValue={condition.operator ?? null}
                      label={`${t("combinationRules.condition")} *`}
                      options={operatorOptions}
                      labelInValue
                      disabled={isView}
                      error={
                        rowErrors?.operator?.value?.message ??
                        rowErrors?.operator?.message
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Field
                      type="input"
                      name={`${name}[${conditionIndex}].value`}
                      control={control}
                      defaultValue={condition.value ?? ""}
                      label={`${t("combinationRules.value")} *`}
                      inputMode="numeric"
                      disabled={isView}
                      error={rowErrors?.value?.message}
                      onInput={(event) => {
                        const input = (
                          event as React.FormEvent<HTMLInputElement>
                        ).currentTarget;
                        const value = input.value.replace(/\D/g, "");
                        input.value = value;
                        control.setValue(
                          `${name}[${conditionIndex}].value`,
                          value,
                        );
                      }}
                    />
                  </div>
                </Flex>
              )}
            </Flex>
          </React.Fragment>
        );
      })}

      {!isView &&
        usedTypes.length < COMBINATION_RULE_CONDITION_TYPES.length && (
          <div>
            <Button
              variant="text"
              color="primary"
              fontSize="12"
              size="small"
              htmlType="button"
              icon={<PlusIcon size={16} />}
              onClick={() => append(emptyCondition())}
            >
              {t("combinationRules.add_condition")}
            </Button>
          </div>
        )}
    </Flex>
  );
};

export default GroupConditions;
